import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../api/endpoints.js';
import { Page } from '../../components/AppShell.jsx';
import {
  Badge, Button, Card, DataTable, Dialog, Field, Input, Pagination, Select, Textarea,
} from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { toast } from '../../lib/toast.js';
import { dateNumeric, humanise, minorFromRupees, money, number, phoneWithCountryCode, rupeesFromMinor } from '../../lib/format.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';

const REFUNDABLE = ['succeeded', 'partially_refunded'];

/** Who took the money: the apps sell through Google Play, the website through Cashfree. */
const PROVIDER_LABEL = { googleplay: 'Google Play', cashfree: 'Cashfree' };
const providerLabel = (provider) => PROVIDER_LABEL[provider] || provider || 'Unknown';
const isPlay = (payment) => payment?.provider === 'googleplay';

/**
 * Every credit purchase, newest first — for answering "I paid and got
 * nothing" and for issuing a refund. App purchases go through Google Play,
 * website purchases through Cashfree; each row says which.
 *
 * Search takes the order id (or part of one): a Google Play order number
 * (GPA.…) or a Cashfree order id, whichever the person quotes from their
 * receipt.
 *
 * Google Play refunds whole orders only, so for those the amount is fixed at
 * the full purchase.
 *
 * A refunded credit purchase takes its credits back in proportion to the money
 * returned. Credits already spent cannot be taken back; the result says how
 * many, so the admin knows what the refund actually cost.
 */
export default function PaymentsPage() {
  const { can } = useAuth();
  const canRefund = can(PERMISSIONS.REFUND_ISSUE);
  const queryClient = useQueryClient();

  const [refunding, setRefunding] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState(null);

  const list = useListQuery({
    key: 'admin-payments',
    fetcher: api.finance.payments,
    initialFilters: { status: '' },
    searchable: true,
  });

  const openRefund = (payment) => {
    setRefunding(payment);
    setAmount(rupeesFromMinor(payment.refundableMinor));
    setReason('');
    setOutcome(null);
  };

  const close = () => {
    setRefunding(null);
    setOutcome(null);
  };

  const refund = useMutation({
    mutationFn: () =>
      api.finance.refund(refunding._id, { amountMinor: minorFromRupees(amount), reason: reason.trim() }),
    onSuccess: (result) => {
      toast.success('Refund issued.');
      queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
      // Keep the dialog open on the result when credits were involved, so the
      // shortfall is read rather than flashed in a toast.
      if (result?.credits) setOutcome(result.credits);
      else close();
    },
    onError: (error) => toast.error(error.message || 'That refund could not be issued.'),
  });

  const amountMinor = minorFromRupees(amount);
  const amountError =
    refunding && amount !== '' && (!amountMinor || amountMinor <= 0 || amountMinor > refunding.refundableMinor)
      ? `Between ${money(1)} and ${money(refunding.refundableMinor)}.`
      : null;
  // What the refund will take back, by the same rule the server uses.
  const creditsBack =
    refunding?.credits && amountMinor > 0
      ? Math.floor((refunding.credits * Math.min(amountMinor, refunding.refundableMinor)) / refunding.amountMinor)
      : null;

  const columns = [
    {
      key: 'when',
      header: 'When',
      render: (payment) => <span className="table__muted nowrap">{dateNumeric(payment.createdAt)}</span>,
    },
    {
      key: 'payer',
      header: 'Paid by',
      render: (payment) => (
        <div>
          <div className="table__primary">
            {payment.payer ? (
              payment.app === 'rental' ? (
                <Link className="table__link" to={`/users/${payment.payer.id}`}>
                  {payment.payer.name || 'Unnamed'}
                </Link>
              ) : (
                payment.payer.name || 'Unnamed'
              )
            ) : (
              'Unknown'
            )}
          </div>
          <div className="table__muted mono">{phoneWithCountryCode(payment.payer?.phone)}</div>
        </div>
      ),
    },
    {
      key: 'what',
      header: 'For',
      render: (payment) => (
        <div>
          <div>
            {payment.credits ? `${number(payment.credits)} credits` : humanise(payment.purpose)}
          </div>
          <div className="table__muted">{payment.app === 'broker' ? 'Grihvaas Broker' : 'Grihvaas'}</div>
        </div>
      ),
    },
    {
      key: 'order',
      header: 'Order id',
      render: (payment) => (
        <div>
          <span className="table__muted mono">{payment.providerIntentId || '—'}</span>
          <div className="table__muted">{providerLabel(payment.provider)}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      className: 'table__numeric',
      render: (payment) => (
        <div>
          <div>{money(payment.amountMinor)}</div>
          {payment.refundedAmountMinor ? (
            <div className="table__muted">{money(payment.refundedAmountMinor)} refunded</div>
          ) : null}
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (payment) => <Badge status={payment.status} /> },
    {
      key: 'actions',
      header: '',
      render: (payment) =>
        canRefund && REFUNDABLE.includes(payment.status) && payment.refundableMinor > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              openRefund(payment);
            }}
          >
            Refund
          </Button>
        ) : null,
    },
  ];

  return (
    <Page title="Payments" subtitle="Credit purchases: the apps through Google Play, the website through Cashfree. Newest first.">
      <Card bodyless>
        <div className="card__body">
          <div className="toolbar">
            <div className="toolbar__search">
              <Input
                placeholder="Search by order id"
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
                aria-label="Search payments"
              />
            </div>
            <Select
              value={list.filters.status}
              onChange={(event) => list.setFilter('status', event.target.value)}
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              <option value="succeeded">Paid</option>
              <option value="requires_payment">Not paid</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="partially_refunded">Partly refunded</option>
              <option value="refunded">Refunded</option>
            </Select>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={list.rows}
          query={list.query}
          empty={{
            title: 'No payments here',
            body: 'Nothing matches these filters. A payment appears as soon as someone starts a checkout.',
          }}
        />
        <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
      </Card>

      <Dialog
        open={Boolean(refunding)}
        onClose={close}
        title={outcome ? 'Refund issued' : 'Refund this payment'}
        warning={
          outcome
            ? null
            : isPlay(refunding)
              ? 'Google refunds the whole purchase to the Google Play payment method it came from, usually within a few days. Google Play cannot refund part of an order. Recorded in the audit log against your account. Not reversible.'
              : 'The money goes back through Cashfree to the card or UPI account it came from, within 7 working days, as the refunds policy promises. Recorded in the audit log against your account. Not reversible.'
        }
        footer={
          outcome ? (
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={refund.isPending}
                disabled={Boolean(amountError) || !amountMinor || reason.trim().length < 3}
                onClick={() => refund.mutate()}
              >
                Refund {amountMinor ? money(amountMinor) : ''}
              </Button>
            </>
          )
        }
      >
        {outcome ? (
          <div className="stack">
            <p>
              {number(outcome.reversed)} of the {number(outcome.owed)} credits this refund covers were taken back
              from the account.
            </p>
            {outcome.shortfall > 0 ? (
              <p className="field__error">
                {number(outcome.shortfall)} had already been spent and could not be taken back.
              </p>
            ) : null}
          </div>
        ) : refunding ? (
          <>
            <p className="muted">
              {refunding.payer?.name || 'This person'} paid {money(refunding.amountMinor)}
              {refunding.credits ? ` for ${number(refunding.credits)} credits` : ''}.{' '}
              {money(refunding.refundableMinor)} can still be refunded.
            </p>
            <Field
              label="Amount (₹)"
              error={amountError}
              hint={
                isPlay(refunding)
                  ? 'Google Play refunds the whole purchase only.'
                  : creditsBack != null
                    ? `Takes back ${number(creditsBack)} credits, or as many as are left.`
                    : undefined
              }
            >
              <Input
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                disabled={isPlay(refunding)}
              />
            </Field>
            <Field
              label="Reason"
              hint={
                isPlay(refunding)
                  ? 'Required. Kept in the audit log.'
                  : 'Required. Sent to Cashfree with the refund and kept in the audit log.'
              }
            >
              <Textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Charged twice for the same pack" />
            </Field>
          </>
        ) : null}
      </Dialog>
    </Page>
  );
}
